import { formatPersonName } from '@/utils/personName';
/**
 * ReviewStep — Stage 5.
 *
 * Summarizes the cart before signing: applicants count, products count, total
 * funding, beneficiaries, disclosures acknowledged.  Mirrors
 * flow-account-opening.html View 3 "Ready to submit" right-panel meta.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import type { CartDto, ReviewSummary } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, CollapsibleSection, KVPair, Pill, type BeneficiaryEntry } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { formatCurrencyUsd, formatIsoDate } from '../utils/formatting';
import { formatSsn } from '@/utils/ssn';
import { useWorkflowGates } from '../hooks/useWorkflowGates';
import { DeclineApplicationModal } from '../components/DeclineApplicationModal';
import { PreSubmitConfirmModal, type PreSubmitConfirmSummary } from '../components/PreSubmitConfirmModal';
// BACKLOG-0746 — per-person readiness line "Open person →" reuses the SAME
// person-open ceremony PeopleStep uses: PdwShell (Personal cohort, edit-entry)
// / PersonDialog dispatcher (non-Personal), branched via findGroupForType.
import { PdwShell } from '../components/personDialog/PdwShell';
import { PersonDialog } from '../components/personDialog/PersonDialog';
import { findGroupForType } from '../components/personDialog/findGroupForType';
import { normalizeOfacStatus } from '../components/personDialog/shared/ofacStatus';
import {
  isExistingCustomer,
  deriveVerifyTabStatus,
  DEFAULT_ID_EXPIRATION_WARNING_DAYS,
} from '../components/personDialog/shared/existingCustomer';
// Named-blocker panel + quick-fix micro-modal (Bill 2026-07-23) — the blocked
// banner now NAMES what's blocking and fixes small items in place.
import { GateBlockerPanel, GateQuickFixModal } from '../components/compliance/GateFixPanel';
import {
  buildOfacExtraPatch,
  buildOfacScreenName,
  performOfacScan,
  readOfacStaleDays,
} from '../components/personDialog/shared/ofacAutoScan';
import { useConfig } from '@/contexts/ConfigContext';
import { useApi } from '../hooks/useApi';
import type { RaoPerson } from '../types';
import { reviewFeatureLabels } from './reviewFeatures';

export interface ReviewStepProps {
  cart: CartDto;
  /**
   * Raised after a successful Submit Application (PreSubmitConfirmModal
   * confirm → /signing/start → /v3/carts/{id}/commit). CartDetail wires
   * this to its cart re-fetch so the SignStep/CommitFlow tracker mounts.
   * (S1.1 punch-list 2026-06-11 — without it the FSR was stranded on a
   * stale 'draft' view after the commit kicked off.)
   */
  onCartUpdated?: (cart: CartDto) => void;
}

interface ReviewCounts {
  applicants: number;
  joints: number;
  products: number;
  beneficiaries: number;
  totalFunding: number;
}

interface ProductBenfSummary {
  /** metadataJson.beneficiaries map key — line-item INSTANCE ID (row-store P2; legacy key == productCode). */
  key: string;
  /** Product code resolved via the matching productsJson line, with a "#n" ordinal when the code appears on 2+ lines. */
  displayLabel: string;
  primaryCount: number;
  contingentCount: number;
  primaryTotal: number;
  contingentTotal: number;
  businessCount: number;
  businessTotal: number;
  valid: boolean;
}

/**
 * Row-store P2 (2026-07-06): cart.metadataJson.beneficiaries is keyed by
 * line-item INSTANCE ID going forward (legacy carts keyed by productCode —
 * still valid because the backfill set instanceId == productCode). Resolve
 * each key to a human label through the productsJson lines so two instances
 * of the same code render as "CODE #1" / "CODE #2" instead of colliding.
 */
function buildLineLabelIndex(cart: CartDto): Map<string, string> {
  const labels = new Map<string, string>();
  if (!cart.productsJson) return labels;
  try {
    const raw = JSON.parse(cart.productsJson) as unknown;
    if (!Array.isArray(raw)) return labels;
    const lines = raw
      .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
      .map((p) => ({
        code: typeof p['code'] === 'string' ? p['code'] : '',
        instanceId: typeof p['instanceId'] === 'string' && p['instanceId'].length > 0
          ? p['instanceId']
          : undefined,
      }))
      .filter((p) => p.code.length > 0);
    const codeCounts = new Map<string, number>();
    for (const l of lines) codeCounts.set(l.code, (codeCounts.get(l.code) ?? 0) + 1);
    const seen = new Map<string, number>();
    for (const l of lines) {
      const n = (seen.get(l.code) ?? 0) + 1;
      seen.set(l.code, n);
      const key = l.instanceId ?? l.code;
      labels.set(key, (codeCounts.get(l.code) ?? 0) > 1 ? `${l.code} #${n}` : l.code);
    }
  } catch { /* empty index — keys fall back to themselves */ }
  return labels;
}

/**
 * Per-line-item ownership index (2026-07-07). Ownership is a PER-PRODUCT
 * fact (CartPartyRoles → productsJson.primaryOwnerId / jointOwnerIds), not a
 * per-person scalar — person.role stays 'primary' for everyone added as an
 * applicant, which is why the old Review showed BOTH owners as "Primary" and
 * "Joint owners: 0" on a two-account cross-owned cart.
 */
interface OwnershipIndex {
  /** personId → ["Primary — FREE #1", "Joint — FREE #2", …] in line order. */
  roleLinesByPersonId: Map<string, string[]>;
  /** Distinct person ids appearing as a joint owner on ANY line. */
  jointOwnerIds: Set<string>;
  /** Product lines carrying ownership data (0 ⇒ legacy cart, use fallbacks). */
  lineCount: number;
  /** Lines with a primary owner assigned. */
  linesWithPrimary: number;
}

function buildOwnershipIndex(cart: CartDto): OwnershipIndex {
  const idx: OwnershipIndex = {
    roleLinesByPersonId: new Map(),
    jointOwnerIds: new Set(),
    lineCount: 0,
    linesWithPrimary: 0,
  };
  if (!cart.productsJson) return idx;
  try {
    const raw = JSON.parse(cart.productsJson) as unknown;
    if (!Array.isArray(raw)) return idx;
    const labels = buildLineLabelIndex(cart);
    const add = (personId: string, line: string): void => {
      const arr = idx.roleLinesByPersonId.get(personId) ?? [];
      arr.push(line);
      idx.roleLinesByPersonId.set(personId, arr);
    };
    for (const x of raw) {
      if (!x || typeof x !== 'object') continue;
      const p = x as Record<string, unknown>;
      const code = typeof p['code'] === 'string' ? p['code'] : '';
      if (!code) continue;
      idx.lineCount += 1;
      const key = typeof p['instanceId'] === 'string' && p['instanceId'].length > 0
        ? p['instanceId'] : code;
      const label = labels.get(key) ?? code;
      const primaryId = typeof p['primaryOwnerId'] === 'string' ? p['primaryOwnerId'] : '';
      if (primaryId) {
        idx.linesWithPrimary += 1;
        add(primaryId, `Primary — ${label}`);
      }
      const joints = Array.isArray(p['jointOwnerIds']) ? (p['jointOwnerIds'] as unknown[]) : [];
      for (const j of joints) {
        if (typeof j === 'string' && j.length > 0) {
          idx.jointOwnerIds.add(j);
          add(j, `Joint — ${label}`);
        }
      }
    }
  } catch { /* empty index — render falls back to flat person.role */ }
  return idx;
}

function summarizeBeneficiaries(cart: CartDto): ProductBenfSummary[] {
  if (!cart.metadataJson) return [];
  try {
    const meta = JSON.parse(cart.metadataJson) as Record<string, unknown>;
    const raw = meta['beneficiaries'];
    if (!raw || typeof raw !== 'object') return [];
    const lineLabels = buildLineLabelIndex(cart);
    const result: ProductBenfSummary[] = [];
    for (const [key, entriesRaw] of Object.entries(raw as Record<string, unknown>)) {
      if (!Array.isArray(entriesRaw)) continue;
      const entries = entriesRaw as BeneficiaryEntry[];
      const primaries   = entries.filter((e) => e.role === 'primary');
      const contingents = entries.filter((e) => e.role === 'contingent');
      const business    = entries.filter((e) =>
        e.role === 'member' || e.role === 'owner' || e.role === 'trustee',
      );
      const sum = (arr: BeneficiaryEntry[]): number =>
        Math.round(arr.reduce((s, e) => s + (Number.isFinite(e.percentage) ? e.percentage : 0), 0) * 100) / 100;
      const primaryTotal = sum(primaries);
      const contingentTotal = sum(contingents);
      const businessTotal = sum(business);
      const primaryValid = primaries.length === 0 || primaryTotal === 100;
      const contingentValid = contingents.length === 0 || contingentTotal === 100;
      const businessValid = businessTotal <= 100;
      result.push({
        key,
        // Legacy fallback: a key with no matching line renders verbatim (it IS
        // the product code on pre-row-store carts).
        displayLabel: lineLabels.get(key) ?? key,
        primaryCount: primaries.length,
        contingentCount: contingents.length,
        primaryTotal,
        contingentTotal,
        businessCount: business.length,
        businessTotal,
        valid: primaryValid && contingentValid && businessValid,
      });
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * Per-PRODUCT review tile (Bill 2026-07-13 Item 19) — everything an officer
 * confirms at a glance for ONE product line: type, primary owner, joint
 * owners, POD/beneficiary configuration, and selected features. Replaces the
 * old aggregate Applicants/Products/Beneficiaries split (which mirrored the
 * legacy single-product SharePoint-list shape and is meaningless in a
 * multi-product cart). Assembled entirely from the parsed cart — no API.
 */
interface ProductTileData {
  key: string;
  label: string;
  openingDeposit?: number;
  primaryOwner?: string;
  jointOwners: string[];
  features: string[];
  benf?: ProductBenfSummary;
}

function buildProductTiles(
  cart: CartDto,
  personNameById: Map<string, string>,
  benfByKey: Map<string, ProductBenfSummary>,
): ProductTileData[] {
  if (!cart.productsJson) return [];
  let raw: unknown;
  try { raw = JSON.parse(cart.productsJson); } catch { return []; }
  if (!Array.isArray(raw)) return [];
  const labels = buildLineLabelIndex(cart);
  const tiles: ProductTileData[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const p = x as Record<string, unknown>;
    const code = typeof p['code'] === 'string' ? p['code'] : '';
    if (!code) continue;
    const key = typeof p['instanceId'] === 'string' && p['instanceId'].length > 0
      ? (p['instanceId'] as string) : code;
    const primaryId = typeof p['primaryOwnerId'] === 'string' ? p['primaryOwnerId'] : '';
    const jointIds = Array.isArray(p['jointOwnerIds']) ? (p['jointOwnerIds'] as unknown[]) : [];
    const fv = (p['featureValues'] && typeof p['featureValues'] === 'object')
      ? (p['featureValues'] as Record<string, unknown>) : {};
    const features = reviewFeatureLabels(fv);
    const depositRaw = p['openingDeposit'];
    const deposit = typeof depositRaw === 'number' ? depositRaw
      : typeof depositRaw === 'string' && depositRaw !== '' ? Number(depositRaw) : undefined;
    tiles.push({
      key,
      label: labels.get(key) ?? code,
      openingDeposit: Number.isFinite(deposit) ? deposit : undefined,
      primaryOwner: primaryId ? (personNameById.get(primaryId) ?? primaryId) : undefined,
      jointOwners: jointIds
        .filter((j): j is string => typeof j === 'string' && j.length > 0)
        .map((j) => personNameById.get(j) ?? j),
      features,
      benf: benfByKey.get(key),
    });
  }
  return tiles;
}

function countCart(cart: CartDto): ReviewCounts {
  let applicants = 0;
  let joints = 0;
  let beneficiaries = 0;
  if (cart.peopleJson) {
    try {
      const raw = JSON.parse(cart.peopleJson) as unknown;
      let arr: Array<Record<string, unknown>> = [];
      if (Array.isArray(raw)) arr = raw as Array<Record<string, unknown>>;
      else if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
        arr = (raw as { people: Array<Record<string, unknown>> }).people;
      }
      for (const p of arr) {
        const role = typeof p['role'] === 'string' ? p['role'] : '';
        if (role === 'primary' || role === 'joint') applicants += 1;
        if (role === 'joint') joints += 1;
        if (role === 'beneficiary' || role === 'pod') beneficiaries += 1;
      }
    } catch { /* swallow */ }
  }

  let products = 0;
  let totalFunding = 0;
  if (cart.productsJson) {
    try {
      const raw = JSON.parse(cart.productsJson);
      if (Array.isArray(raw)) {
        for (const p of raw as Array<Record<string, unknown>>) {
          if (typeof p['code'] === 'string') {
            products += typeof p['qty'] === 'number' && p['qty'] > 0 ? p['qty'] : 1;
          }
          const fund = p['openingDeposit'];
          if (typeof fund === 'number') totalFunding += fund;
          else if (typeof fund === 'string') totalFunding += Number.parseFloat(fund) || 0;
        }
      }
    } catch { /* swallow */ }
  }

  return { applicants, joints, products, beneficiaries, totalFunding };
}

// ---------------------------------------------------------------------------
// Cart-state parsing — mirrors SignStep.tsx, kept local so ReviewStep doesn't
// have to reach into the Sign step module for wiring.
// ---------------------------------------------------------------------------

function parsePeopleLocal(cart: CartDto): ReadonlyArray<RaoPerson> {
  if (!cart.peopleJson) return [];
  try {
    const raw = JSON.parse(cart.peopleJson) as unknown;
    let arr: unknown[] = [];
    if (Array.isArray(raw)) arr = raw;
    else if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
      arr = (raw as { people: unknown[] }).people;
    }
    return arr.filter((p): p is RaoPerson => !!p && typeof p === 'object');
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Per-person readiness line (BACKLOG-0746, Bill 2026-06-12).
// Locked contract: docs/operations/person-modal-inventory/
// 07-CART-REVIEW-CLEANUP-MOCKUP.html. Replaces the deleted ComplianceStep
// zombie. READ-ONLY tokens computed from values the Person Modal already
// settled in cart.peopleJson — ZERO API calls, nothing re-runs here:
//   · CIP        — person.extra.idType + person.extra.idNumber (the PDW CIP id
//                  family, CD F-254/255; same derivation as
//                  PersonStatusPills.deriveCip)
//   · OFAC       — person.extra.ofacStatus via normalizeOfacStatus (written by
//                  the PDW OfacScanSection)
//   · FraudPoint — person.amlInsightScore, 750/500 bands per the locked
//                  procedure (Low ≥750 · Moderate 500-749 · High <500)
//   · CDD        — derived: CIP satisfied AND OFAC not a current hit
// Amber/red states deep-link back into that person's modal — the ONLY place
// the fix happens ("Review only summarizes", Bill 2026-06-01).
// ---------------------------------------------------------------------------

type ReadinessTone = 'ok' | 'warn' | 'err' | 'info' | 'muted';

interface ReadinessChip {
  key: string;
  label: string;
  tone: ReadinessTone;
  title: string;
}

function readExtraString(p: RaoPerson, key: string): string | undefined {
  const x = p.extra as Record<string, unknown> | undefined;
  const v = x?.[key];
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

function deriveReadiness(p: RaoPerson): { chips: ReadinessChip[]; needsAttention: boolean } {
  const chips: ReadinessChip[] = [];

  // Existing customers carry a prior-verified baseline (Bill v0.70.0 walk +
  // BACKLOG-0513/0835): CIP-EXPIRATION is the only standing gate, and LN/OFAC
  // are NOT re-required, so their absence must never paint a warn/err badge.
  // The fresh-add path (below) is unchanged.
  const existing = isExistingCustomer(p);

  // CIP — a fresh add needs both halves of the CIP id family captured; an
  // existing customer is judged on ID EXPIRATION, not re-capture.
  const idType = readExtraString(p, 'idType');
  const idNumber = readExtraString(p, 'idNumber');
  if (existing) {
    const v = deriveVerifyTabStatus(p, DEFAULT_ID_EXPIRATION_WARNING_DAYS);
    if (v.status === 'red') {
      chips.push({ key: 'cip', label: 'CIP — ID expired', tone: 'err', title: v.reason });
    } else if (v.status === 'yellow' && /update recommended/i.test(v.reason)) {
      // C2: expired within the admin grace window (default 6mo) — allow + advise.
      chips.push({ key: 'cip', label: 'CIP — update recommended', tone: 'warn', title: v.reason });
    } else if (v.status === 'yellow' && /expir/i.test(v.reason)) {
      chips.push({ key: 'cip', label: 'CIP — ID expiring', tone: 'warn', title: v.reason });
    } else {
      chips.push({ key: 'cip', label: 'CIP on file', tone: 'ok', title: 'Existing customer — CIP on file at Core Director; ID not expired.' });
    }
  } else if (idType && idNumber) {
    chips.push({ key: 'cip', label: 'CIP ✓', tone: 'ok', title: `ID on file (type ${idType}) — captured in the Person Modal.` });
  } else {
    chips.push({ key: 'cip', label: 'CIP incomplete', tone: 'warn', title: 'ID Type + ID Number not fully captured — open the person to finish CIP.' });
  }

  // OFAC — settled result only (the PDW owns the scan; nothing re-runs here).
  const ofac = normalizeOfacStatus(readExtraString(p, 'ofacStatus'));
  if (ofac === 'clear') {
    chips.push({ key: 'ofac', label: 'OFAC clear', tone: 'ok', title: 'OFAC screen returned no matches.' });
  } else if (ofac === 'hit-current') {
    chips.push({ key: 'ofac', label: 'OFAC hit', tone: 'err', title: 'Active sanctions match — Risk Management review required.' });
  } else if (ofac === 'archived-only') {
    chips.push({ key: 'ofac', label: 'OFAC archived match', tone: 'warn', title: 'Historical/delisted match only — submit allowed, audit-logged.' });
  } else if (ofac === 'pending') {
    chips.push({ key: 'ofac', label: 'OFAC pending', tone: 'warn', title: 'OFAC scan running or queued.' });
  } else if (!existing) {
    // Only a fresh add is REQUIRED to run OFAC — an existing customer trusts
    // the prior-pass baseline (no badge rather than a false "not run" warn).
    chips.push({ key: 'ofac', label: 'OFAC not run', tone: 'warn', title: 'No OFAC screen on record — open the person to run it.' });
  }

  // CDD — for an individual retail customer, Customer Due Diligence is
  // satisfied once CIP is on file and OFAC shows no active hit (Bill v0.70.0
  // walk: CDD now lives on the person tile, not a separate gates section).
  // Derived from the two chips just computed — no new data source.
  const cipSatisfied = chips.some((c) => c.key === 'cip' && c.tone === 'ok');
  if (cipSatisfied && ofac !== 'hit-current') {
    chips.push({ key: 'cdd', label: 'CDD ✓', tone: 'ok', title: 'Customer Due Diligence satisfied — CIP on file and OFAC clear.' });
  }

  // FraudPoint — band + score from the LN report the officer already ran.
  const score = p.amlInsightScore;
  if (typeof score === 'number') {
    if (score >= 750) {
      chips.push({ key: 'fp', label: `FraudPoint LOW · ${score}`, tone: 'ok', title: 'FraudPoint 750+ — proceed.' });
    } else if (score >= 500) {
      chips.push({ key: 'fp', label: `FraudPoint MODERATE · ${score}`, tone: 'warn', title: 'FraudPoint 500-749 — collect rules apply; supervisor review.' });
    } else {
      chips.push({ key: 'fp', label: `FraudPoint HIGH · ${score}`, tone: 'err', title: 'FraudPoint below 500 — decline band.' });
    }
  } else if (!existing) {
    // FraudPoint is NOT re-required for existing customers (Bill: "if it is
    // not required, do not show badge"). Fresh add still needs a run.
    chips.push({ key: 'fp', label: 'FraudPoint — not run', tone: 'warn', title: 'No LexisNexis report on record — open the person to run it.' });
  }

  // Documents removed from Review (Bill v0.70.0 walk) — the PDW grid owns the
  // document slots; Review no longer surfaces a per-person doc count.

  return { chips, needsAttention: chips.some((c) => c.tone === 'warn' || c.tone === 'err') };
}

function personInitials(p: RaoPerson): string {
  const name = (p.fullName || `${p.firstName ?? ''} ${p.lastName ?? ''}`).trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
}

const ROLE_LABELS: Record<string, string> = {
  primary: 'Primary',
  joint: 'Joint',
  beneficiary: 'Beneficiary',
  pod: 'POD beneficiary',
  custodian: 'Custodian',
  signer: 'Signer',
};

function personRoleLine(p: RaoPerson, ownership?: OwnershipIndex): string {
  // Per-line ownership (rows-mode carts): "Primary — FREE #1 · Joint — FREE #2".
  // Falls back to the flat person.role for legacy carts with no ownership data.
  const perLine = p.id ? ownership?.roleLinesByPersonId.get(p.id) : undefined;
  const role = perLine && perLine.length > 0
    ? perLine.join(' · ')
    : (ROLE_LABELS[p.role] ?? p.role ?? '');
  const qualifier = p.citizenshipStatus || p.customerType || '';
  return qualifier ? `${role} · ${qualifier}` : role;
}

/**
 * Identification line (Bill 2026-07-02) — a name alone can't distinguish a
 * dad and son with the same first/last. DOB + SSN + CIF are the same anchors
 * the People-step tiles show, so the read-back is unambiguous.
 */
function personIdentityLine(p: RaoPerson): string {
  return [
    p.dob ? `DOB ${formatIsoDate(p.dob)}` : '',
    p.taxId ? `SSN ${formatSsn(p.taxId)}` : '',
    // Guard: cifId has been observed arriving as an OBJECT in peopleJson
    // (rendered "CIF [object Object]", Bill 2026-07-23) — display defensively;
    // the writer trace is a separate backlog item.
    p.cifId
      ? `CIF ${typeof p.cifId === 'string' ? p.cifId : String((p.cifId as unknown as { value?: unknown }).value ?? '')}`.trim()
      : 'New customer',
  ].filter(Boolean).join(' · ');
}

/** Full display name incl. middle — falls back to the stored fullName. */
function personDisplayName(p: RaoPerson): string {
  return (
    [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ')
    || p.fullName
    || '(unnamed)'
  );
}

export function ReviewStep({ cart, onCartUpdated }: ReviewStepProps): JSX.Element {
  const navigate = useNavigate();
  const client = useApiClient();
  const { makeSignal } = useApi();
  const [showDecline, setShowDecline] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  // The pinned footer's action slot (CartDetail) - looked up after mount; it renders on the final stage.
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setFooterSlot(document.getElementById('rao-footer-actions')); }, []);
  // Bill 2026-05-12 Tier 2 close-loop — live review summary (chips +
  // per-subtype resolved gate badges + pending-deferral count). Fetched
  // on mount; refetched on cart.version change so admin edits show up.
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [reviewSummaryErr, setReviewSummaryErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await client.retail.carts.reviewSummary(cart.cartId);
        if (!cancelled) {
          if (r.success && r.data) { setReviewSummary(r.data); setReviewSummaryErr(null); }
          else setReviewSummaryErr(r.error ?? 'review summary unavailable');
        }
      } catch (e: unknown) {
        if (!cancelled) setReviewSummaryErr(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [client, cart.cartId, cart.version]);
  const counts = useMemo(() => countCart(cart), [cart]);
  const ownership = useMemo(() => buildOwnershipIndex(cart), [cart]);
  const benfSummaries = useMemo(() => summarizeBeneficiaries(cart), [cart]);
  const totalBenfEntries = benfSummaries.reduce(
    (s, r) => s + r.primaryCount + r.contingentCount + r.businessCount,
    0,
  );
  const anyInvalid = benfSummaries.some((s) => !s.valid);

  // Per-product tiles (Item 19) — assemble from the same parsed inputs.
  const productTiles = useMemo(() => {
    const nameById = new Map<string, string>();
    for (const p of parsePeopleLocal(cart)) {
      const nm = formatPersonName(p);
      if (p.id && nm) nameById.set(p.id, nm);
    }
    const benfByKey = new Map(benfSummaries.map((b) => [b.key, b]));
    return buildProductTiles(cart, nameById, benfByKey);
  }, [cart, benfSummaries]);

  // BACKLOG-0018 Gap 2 — pass reviewSummary so the engine applies per-(customerType
  // × gateKey) admin enforcement strategies (warn-only / deferred / officer-override).
  // On first render reviewSummary is null and the engine evaluates with code-side
  // defaults; once the live summary arrives the memo re-runs with strategies applied.
  const { report: gateReport } = useWorkflowGates(cart, reviewSummary);

  // People parsing drives the per-person readiness chips + the person-open
  // ceremony below. (Disclosure-package preview removed from Review per Bill's
  // v0.70.0 walk — disclosures don't belong on Review & Submit.)
  const people = useMemo(() => parsePeopleLocal(cart), [cart]);

  // ── BACKLOG-0746 — readiness-line "Open person →" ceremony ──────────────
  // Reuses PeopleStep's person-open mechanism: Personal cohort → PdwShell
  // (entryMode='edit', skips Stages 1-4, opens at the seeded review); every
  // other cohort → the legacy PersonDialog dispatcher in 'edit' mode. The
  // save path mirrors PeopleStep.onModalSave's update-in-place semantics
  // (stable person.id first, then cifId, then fullName) but never appends —
  // Review can only edit a person who is already on the cart. Persistence is
  // the row-store per-party PATCH command (row-store P2, 2026-07-06).
  const [openPersonTarget, setOpenPersonTarget] = useState<RaoPerson | null>(null);
  const [personSaveError, setPersonSaveError] = useState<string | null>(null);
  const cartEditable = cart.status === 'draft' || cart.status === 'active';
  const openIsPersonal =
    !!openPersonTarget && findGroupForType(openPersonTarget.customerType) === 'personal';
  const reviewCifIds = useMemo(
    () => people.map((p) => p.cifId).filter((id): id is string => !!id),
    [people],
  );

  /**
   * Core per-party persist (row-store P2) — shared by the person-open
   * ceremony, the GateQuickFixModal save, and the blocker-panel OFAC run.
   * Matches the edited person back to a cart row (stable id → cifId →
   * fullName), PATCHes /v3/carts/{cartId}/parties/{partyKey}, and raises
   * onCartUpdated so gates + chips re-evaluate from the returned CartDto.
   * Returns true on success.
   */
  const persistPersonPatch = async (saved: RaoPerson, target?: RaoPerson | null): Promise<boolean> => {
    const targetId = saved.id ?? target?.id;
    const idx = targetId
      ? people.findIndex((p) => p.id === targetId)
      : saved.cifId
        ? people.findIndex((p) => p.cifId === saved.cifId)
        : people.findIndex((p) => p.fullName === (target?.fullName ?? saved.fullName));
    if (idx < 0) {
      setPersonSaveError('Could not match the edited person back to the cart — re-open from the People step.');
      return false;
    }
    // partyKey is the person's stable clientId (person.id); Review never
    // appends, so the matched cart row must already carry one (post-backfill
    // every composed person does).
    const partyKey = targetId ?? people[idx]?.id;
    if (!partyKey) {
      setPersonSaveError('This person is missing a stable cart id — re-open from the People step.');
      return false;
    }
    const withId: RaoPerson = !saved.id ? { ...saved, id: partyKey } : saved;
    try {
      const res = await client.retail.carts.updateParty(
        cart.cartId,
        partyKey,
        { person: withId as unknown as Record<string, unknown> },
        { signal: makeSignal() },
      );
      if (res.success && res.cart) {
        setPersonSaveError(null);
        onCartUpdated?.(res.cart);
        return true;
      }
      setPersonSaveError(res.error ?? 'Person save failed — reload the cart and retry.');
      return false;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return false;
      setPersonSaveError(err instanceof Error ? err.message : 'Person save failed');
      return false;
    }
  };

  const onReadinessPersonSave = async (saved: RaoPerson): Promise<void> => {
    const target = openPersonTarget;
    setOpenPersonTarget(null);
    await persistPersonPatch(saved, target);
  };

  // ── GateBlockerPanel wiring (Bill 2026-07-23) ────────────────────────────
  const { config } = useConfig();
  const ofacStaleDays = useMemo(
    () => readOfacStaleDays(config.systemSettings as Record<string, unknown> | undefined),
    [config.systemSettings],
  );
  const [quickFixTarget, setQuickFixTarget] = useState<RaoPerson | null>(null);
  const [quickFixSaving, setQuickFixSaving] = useState<boolean>(false);
  const [quickFixError, setQuickFixError] = useState<string | null>(null);
  const [runningOfacKey, setRunningOfacKey] = useState<string | null>(null);

  const onQuickFixSave = async (updated: RaoPerson): Promise<void> => {
    setQuickFixSaving(true);
    setQuickFixError(null);
    try {
      const ok = await persistPersonPatch(updated, quickFixTarget);
      if (ok) {
        setQuickFixTarget(null);
      } else {
        setQuickFixError('Save failed — see the message above the applicant list, then retry.');
      }
    } finally {
      setQuickFixSaving(false);
    }
  };

  /** Blocker-panel "Run OFAC now" — the same headless scan + persistence the
   *  PdwShell save-time auto-fire uses, PATCHed straight onto the cart row. */
  const onRunOfacForPerson = async (p: RaoPerson): Promise<void> => {
    const key = p.id ?? p.cifId ?? `${p.fullName ?? 'person'}`;
    setRunningOfacKey(key);
    try {
      const name = buildOfacScreenName(p);
      if (name.length === 0) {
        setPersonSaveError('This person has no name on the cart — quick-fix the name first, then run OFAC.');
        return;
      }
      const outcome = await performOfacScan(client, name, 'Indv', {
        birthDate: p.dob || undefined,
        cartId: cart.cartId,
        personKey: p.id || undefined,
      });
      const extra = (p as unknown as { extra?: Record<string, unknown> }).extra;
      const nextExtra = buildOfacExtraPatch(extra, outcome.status, outcome.matches, outcome.records, outcome.screening);
      await persistPersonPatch({ ...p, extra: nextExtra } as RaoPerson, p);
    } catch (err) {
      setPersonSaveError(err instanceof Error ? err.message : 'OFAC screen failed — try again from the person modal.');
    } finally {
      setRunningOfacKey(null);
    }
  };

  return (
    <section aria-labelledby="rao-review-h" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-8)' }}>
      <h2 id="rao-review-h" style={{ fontSize: 18, color: 'var(--pcb-blue-dark)' }}>
        Review &amp; submit
      </h2>

      {/* Bill 2026-05-12 Round 2 — Review-stage aggregate chip row (Screen 26).
          Admin-configured chips from ReviewAggregateChip surface here; chips
          live-update on cart.version change. Per-subtype resolved gate
          strategies render below as a hover-citation badge row. */}
      {reviewSummary && reviewSummary.chips.length > 0 && (
        <div role="region" aria-label="Review aggregate chips" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {reviewSummary.chips.map((c) => (
            <span key={c.chipKey} title={c.description ?? ''}><Pill variant="info">{c.displayLabel}</Pill></span>
          ))}
          {reviewSummary.pendingDeferrals > 0 && (
            <span title={`${reviewSummary.pendingDeferrals} pending deferred-gate tracking rows`}>
              <Pill variant="warn">⏳ {reviewSummary.pendingDeferrals} pending deferral{reviewSummary.pendingDeferrals === 1 ? '' : 's'}</Pill>
            </span>
          )}
        </div>
      )}
      {/* 2026-07-07 (Bill: "what the heck is that mess of red/yellow pills?")
          — the per-(customerType × gateKey) enforcement-strategy trace strip
          is admin/diagnostic introspection, not FSR review content, and it
          rendered ~44 config pills above the Applicants list. Removed from
          this screen; the same data drives useWorkflowGates below and stays
          browsable in the Admin Panel (gate-keys reference). */}
      {reviewSummaryErr && <Banner tone="info">Live review summary unavailable: {reviewSummaryErr}</Banner>}
      <p style={{ fontSize: 12, color: 'var(--ink-600)' }}>
        Confirm everything below is correct.  Signatures are captured next — after signing the pipeline commits accounts to Core Director.
      </p>

      <CollapsibleSection title="Applicants" iconColor="var(--ent-relationship)" meta={`${counts.applicants} total`}>
        {/* BACKLOG-0746 per-person readiness line — replaces the deleted
            ComplianceStep zombie (locked contract: 07-CART-REVIEW-CLEANUP-
            MOCKUP.html). Read-only chips from cart.peopleJson values the
            Person Modal settled (extra.idType/idNumber · extra.ofacStatus ·
            amlInsightScore · extra.attachedDocs) — zero API calls; amber rows
            deep-link back into that person's modal. */}
        {people.length > 0 && (
          <div className="rao-review-people" role="list" aria-label="Per-person readiness">
            {people.map((p, i) => {
              const { chips, needsAttention } = deriveReadiness(p);
              return (
                <div
                  key={p.id ?? p.cifId ?? `${p.fullName}-${i}`}
                  role="listitem"
                  className={`rao-review-person${needsAttention ? ' rao-review-person--attention' : ''}`}
                >
                  <div className="rao-review-person__avatar" aria-hidden="true">{personInitials(p)}</div>
                  <div className="rao-review-person__who">
                    <div className="rao-review-person__name">{personDisplayName(p)}</div>
                    <div className="rao-review-person__role">{personRoleLine(p, ownership)}</div>
                    <div className="rao-review-person__role">{personIdentityLine(p)}</div>
                  </div>
                  <div className="rao-review-person__chips">
                    {chips.map((c) => (
                      <span key={c.key} title={c.title}><Pill variant={c.tone}>{c.label}</Pill></span>
                    ))}
                  </div>
                  {needsAttention && cartEditable && (
                    <button
                      type="button"
                      className="rao-review-person__open"
                      onClick={() => { setPersonSaveError(null); setOpenPersonTarget(p); }}
                      title={`Open ${p.fullName || 'this person'} in the Person Modal to resolve`}
                    >
                      Open person →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {personSaveError && (
          <div style={{ marginBottom: 'var(--sp-4)' }}>
            <Banner tone="err">{personSaveError}</Banner>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-6)' }}>
          {/* Ownership is per PRODUCT LINE (rows mode) — these read the
              productsJson ownership + metadataJson.beneficiaries the row
              store composes, not the flat person.role that pinned every
              applicant to "Primary" and zeroed the joint/benf counts. */}
          <KVPair
            label="Primary owner"
            value={ownership.lineCount > 0
              ? (ownership.linesWithPrimary === ownership.lineCount
                ? 'Every account'
                : `${ownership.linesWithPrimary} of ${ownership.lineCount} accounts`)
              : (counts.applicants - counts.joints > 0 ? 'Yes' : 'Missing')}
          />
          <KVPair
            label="Joint owners"
            value={ownership.lineCount > 0 ? ownership.jointOwnerIds.size : counts.joints}
          />
          <KVPair label="Beneficiaries / POD" value={totalBenfEntries} />
        </div>
      </CollapsibleSection>

      {/* Per-product tiles (Bill 2026-07-13 Item 19) — one card per product
          with everything to confirm at a glance: type, owners, POD config,
          features. Replaces the old aggregate Products + Beneficiaries
          sections (a single-product-record hallucination). */}
      <CollapsibleSection
        title="Products"
        iconColor="var(--ent-deposit)"
        meta={`${counts.products} · ${reviewCifIds.length} CIF${reviewCifIds.length === 1 ? '' : 's'}${counts.totalFunding > 0 ? ` · ${formatCurrencyUsd(counts.totalFunding)}` : ''}`}
      >
        {productTiles.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>No products in cart yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'var(--sp-5)' }}>
            {productTiles.map((t) => (
              <div
                key={t.key}
                style={{
                  border: '1px solid var(--surface-border)',
                  borderRadius: 'var(--r-md)',
                  background: 'var(--surface-card)',
                  boxShadow: 'var(--sh-1)',
                  padding: 'var(--sp-5)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontSize: 15, color: 'var(--pcb-blue-dark)' }}>{t.label}</strong>
                  {typeof t.openingDeposit === 'number' && t.openingDeposit > 0 && (
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 13 }}>
                      {formatCurrencyUsd(t.openingDeposit)}
                    </span>
                  )}
                </div>

                <div style={{ fontSize: 12, color: 'var(--ink-700)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span><span style={{ color: 'var(--ink-500)' }}>Primary:</span> <strong>{t.primaryOwner ?? '—'}</strong></span>
                  {t.jointOwners.length > 0 && (
                    <span><span style={{ color: 'var(--ink-500)' }}>Joint:</span> {t.jointOwners.join(', ')}</span>
                  )}
                </div>

                {t.features.length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {t.features.map((f) => <Pill key={f} variant="muted">{f}</Pill>)}
                  </div>
                )}

                {t.benf && (t.benf.primaryCount + t.benf.contingentCount + t.benf.businessCount) > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 4, borderTop: '1px solid var(--surface-border)' }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-500)', alignSelf: 'center' }}>POD:</span>
                    {t.benf.primaryCount > 0 && (
                      <Pill variant={t.benf.primaryTotal === 100 ? 'ok' : 'err'}>
                        Primary {t.benf.primaryCount} · {t.benf.primaryTotal}%
                      </Pill>
                    )}
                    {t.benf.contingentCount > 0 && (
                      <Pill variant={t.benf.contingentTotal === 100 ? 'ok' : 'err'}>
                        Contingent {t.benf.contingentCount} · {t.benf.contingentTotal}%
                      </Pill>
                    )}
                    {t.benf.businessCount > 0 && (
                      <Pill variant={t.benf.businessTotal <= 100 ? 'ok' : 'err'}>
                        BO {t.benf.businessCount} · {t.benf.businessTotal}%
                      </Pill>
                    )}
                    {!t.benf.valid && <Pill variant="err">Check %</Pill>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {anyInvalid && (
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <Banner tone="err">
              One or more product beneficiary allocations are invalid — fix on the Products step before signing.
            </Banner>
          </div>
        )}
      </CollapsibleSection>

      {/* Disclosures + Compliance-gates + Documents sections removed from
          Review (Bill v0.70.0 walk): Review consolidates to Applicant tiles
          (compliance gates — CIP/OFAC/CDD now live on each person tile above),
          Product tiles, and Beneficiaries only. Disclosures belong on the
          Disclosure & Upload step, not Review. gateReport is still computed
          (useWorkflowGates) and gates the Ready banner + submit button below. */}

      {gateReport.canCommit ? (
        <Banner tone="ok">
          Ready to continue. The next step builds the disclosure package and determines
          which documents, if any, need a signature. If any do, the application pauses for
          signatures; if none do, the disclosures are delivered and the account opening
          proceeds to commit.
        </Banner>
      ) : (
        <>
          <Banner tone="err">
            Signing is blocked until every item below is resolved (or
            explicitly waived by a Supervisor).
          </Banner>
          {/* Named blockers + targeted in-place fixes (Bill 2026-07-23) —
              replaces the anonymous "resolve each blocker in its source
              step" dead-end that hid MISSING_DOB on Peter's cart. */}
          <GateBlockerPanel
            report={gateReport}
            people={people}
            cartEditable={cartEditable}
            ofacStaleDays={ofacStaleDays}
            onQuickFix={(p) => { setQuickFixError(null); setQuickFixTarget(p); }}
            onRunOfac={(p) => { void onRunOfacForPerson(p); }}
            onOpenPerson={(p) => { setPersonSaveError(null); setOpenPersonTarget(p); }}
            runningOfacKey={runningOfacKey}
          />
        </>
      )}

      {/* Review-stage action row (Bill 2026-05-12 Round 3 SPEC): Decline (red) left, Approve Cart
          (primary, gated by compliance) right. Bill 2026-10-14: it renders INTO the pinned footer
          (CartDetail's #rao-footer-actions slot) so Approve Cart is visible without scrolling;
          inline here only if the slot is absent. */}
      {(() => {
        const actionRow = (
        <div className="rao-review__action-row">
          <div className="rao-review__action-row-left">
            <Button
              variant="ghost"
              onClick={() => setShowDecline(true)}
              style={{ color: 'var(--pcb-red, #b10505)' }}
            >
              ⊘ Decline Cart
            </Button>
          </div>
          <div className="rao-review__action-row-right">
            {/* 2026-07-02 — 'active' is a legitimate submittable working status
                (any touched cart, and every regenerated cart, is 'active'); the
                old status!=='draft' guard permanently dead-ended those. Label
                matches the banner: the NEXT thing that happens is signing. */}
            <Button
              variant="primary"
              onClick={() => setShowSubmit(true)}
              disabled={
                !gateReport.canCommit
                || (cart.status !== 'draft' && cart.status !== 'active')
              }
              title={
                !gateReport.canCommit
                  ? 'Compliance gates must pass before proceeding'
                  : cart.status !== 'draft' && cart.status !== 'active'
                    ? 'This application has already been submitted'
                    : undefined
              }
            >
              Approve Cart →
            </Button>
          </div>
        </div>
        );
        return footerSlot ? createPortal(actionRow, footerSlot) : actionRow;
      })()}

      <DeclineApplicationModal
        open={showDecline}
        cartId={cart.cartId}
        onClose={() => setShowDecline(false)}
        onDeclined={() => { setShowDecline(false); navigate('/rao'); }}
        {...(cart.officerCode ? { officerCode: cart.officerCode } : {})}
      />
      <PreSubmitConfirmModal
        open={showSubmit}
        cart={cart}
        client={client}
        makeSignal={makeSignal}
        summary={({
          applicantsLine: `${counts.applicants} applicant${counts.applicants === 1 ? '' : 's'}`
            + (counts.joints > 0 ? ` · ${counts.joints} joint` : '')
            + (counts.beneficiaries > 0 ? ` · ${counts.beneficiaries} beneficiar${counts.beneficiaries === 1 ? 'y' : 'ies'}` : ''),
          productsLine: counts.products === 0
            ? 'No products in cart'
            : `${counts.products} product${counts.products === 1 ? '' : 's'}`
              + (counts.totalFunding > 0 ? ` · ${formatCurrencyUsd(counts.totalFunding)} total funding` : ''),
          warningCount: gateReport.warnCount,
          deferralCount: 0,
        } satisfies PreSubmitConfirmSummary)}
        onCancel={() => setShowSubmit(false)}
        onSubmitted={(updated) => {
          // Close the modal, then tell the parent to re-fetch — the new
          // status ('awaiting-signatures' after the step-6 pause) mounts
          // SignStep + the CommitFlow tracker. (S1.1 2026-06-11: previously
          // nothing re-fetched here and the FSR was stranded on stale
          // 'draft' after a successful submit.)
          setShowSubmit(false);
          onCartUpdated?.(updated);
        }}
      />

      {/* BACKLOG-0746 — readiness-line person-open ceremony. Same mechanism
          as PeopleStep: Personal cohort → PdwShell edit-entry (skips Stages
          1-4, opens at the seeded review); non-Personal → PersonDialog
          dispatcher in edit mode. Save issues the per-party PATCH command
          (row-store P2) — the server recomposes peopleJson. */}
      {openPersonTarget && openIsPersonal && (
        <PdwShell
          open
          onClose={() => setOpenPersonTarget(null)}
          cartId={cart.cartId}
          cartCifIds={reviewCifIds}
          {...(cart.mode ? { cartMode: cart.mode } : {})}
          onNonPersonalDispatch={() => setOpenPersonTarget(null)}
          onSave={(p) => { void onReadinessPersonSave(p); }}
          entryPerson={openPersonTarget}
          entryMode="edit"
        />
      )}
      {openPersonTarget && !openIsPersonal && (
        <PersonDialog
          open
          mode="edit"
          person={openPersonTarget}
          onSave={(p) => { void onReadinessPersonSave(p); }}
          onClose={() => setOpenPersonTarget(null)}
          cartCifIds={reviewCifIds}
          cartId={cart.cartId}
        />
      )}

      {/* Quick-fix micro-modal — only the person's missing required fields
          (shared REQUIRED_INDIVIDUAL_FIELDS spec). Saves via the same
          per-party PATCH as the person-open ceremony; gates re-evaluate
          from the returned CartDto. */}
      {quickFixTarget && (
        <GateQuickFixModal
          open
          person={quickFixTarget}
          onClose={() => { if (!quickFixSaving) setQuickFixTarget(null); }}
          onSave={onQuickFixSave}
          saving={quickFixSaving}
          error={quickFixError}
        />
      )}
    </section>
  );
}
